import { useBrand } from '@bnnvara/brand';
import { BaseButton } from '@bnnvara/buttons';
import { GridItem } from '@bnnvara/grids';
import { Heading } from '@bnnvara/typography';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

import { FetchMixedMedia } from '../../api/queries/mixedmedia.gql';
import ArticleCard from '../cards/ArticleCard';
import VideoCard from '../cards/VideoCard';
import Link, { useLink } from '../Link';
import PageQuery from '../PageQuery';
import ScrollIntoView from '../ScrollIntoView';
import createPaginator from './helpers/createPaginatorVariables';
import NoTagsOrCategories from './NoTagsOrCategories';

const TagOrCategoryItems = ({ filterType }) => {
  const amountOfItemsPerPage = 8;
  const brand = useBrand();
  const router = useRouter();
  const {
    query: { slug },
  } = router;
  const { setQueryParams } = useLink();

  const convertedTags = slug.split('+');
  const limit = parseInt(router.query.limit, 10) || amountOfItemsPerPage;

  const clickHandler = (fetchMore, pagesEndCursor, mediaEndCursor) => {
    const pagesPaginator = createPaginator(
      filterType === 'tags' ? convertedTags : null,
      filterType === 'categories' ? slug : null,
      limit,
      pagesEndCursor
    );
    const videosPaginator = createPaginator(
      filterType === 'tags' ? convertedTags : null,
      filterType === 'categories' ? slug : null,
      limit,
      mediaEndCursor
    );

    fetchMore({
      variables: {
        pagesPaginator,
        videosPaginator,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const data = {
          ...fetchMoreResult,
        };

        set(
          data,
          'brand.pages.edges',
          uniqBy(
            [
              ...previousResult.brand.pages.edges,
              ...fetchMoreResult.brand.pages.edges,
            ],
            (item) => item.id
          )
        );

        set(
          data,
          'brand.mediaList.edges',
          uniqBy(
            [
              ...previousResult.brand.mediaList.edges,
              ...fetchMoreResult.brand.mediaList.edges,
            ],
            (item) => item.id
          )
        );

        const queryStringLimit = router.query.limit;
        const newLimit = queryStringLimit
          ? parseInt(queryStringLimit, 10) + amountOfItemsPerPage
          : 16;

        setQueryParams({
          limit: newLimit,
        });

        return data;
      },
    });
  };

  return (
    <PageQuery
      query={FetchMixedMedia}
      variables={{
        brandSlug: brand.slug,
        pagesPaginator: createPaginator(
          filterType === 'tags' ? convertedTags : null,
          filterType === 'categories' ? slug : null,
          limit,
          null
        ),
        videosPaginator: createPaginator(
          filterType === 'tags' ? convertedTags : null,
          filterType === 'categories' ? slug : null,
          limit,
          null
        ),
      }}
    >
      {({ data, fetchMore }) => {
        const pages = data.brand.pages.edges;
        const media = data.brand.mediaList.edges;
        const combinedItems = [...pages, ...media];

        const sortedItems = sortBy(combinedItems, [
          (item) => new Date(item.publishDate),
        ]).reverse();

        const items = sortedItems.slice(0, limit);

        const lastMedia = items
          // eslint-disable-next-line no-underscore-dangle
          .map((item) => item.__typename)
          .lastIndexOf('Media');

        const lastPage = items
          // eslint-disable-next-line no-underscore-dangle
          .map((item) => item.__typename)
          .lastIndexOf('Article' || 'ContentPage');

        const { hasNextPage } = data.brand.pages.pageInfo;
        const hasNextMedia = data.brand.mediaList.pageInfo.hasNextPage;
        const pagesEndCursor = lastPage >= 0 ? items[lastPage].id : null;
        const mediaEndCursor = lastMedia >= 0 ? items[lastMedia].id : null;
        let errorText = '';

        if (items.length === 0) {
          if (filterType === 'tags') {
            errorText = `${
              convertedTags.length === 1 ? 'tag' : 'tagcombinatie'
            } ${convertedTags.join(' & ')}`;
          } else {
            errorText = `rubriek "${slug.replace(/-/g, ' ')}"`;
          }
          return (
            <NoTagsOrCategories
              message={`Er zijn helaas geen items gevonden met de ${errorText}.`}
            />
          );
        }

        const matchingTags =
          items[0].tags &&
          items[0].tags
            .filter((tag) => !!convertedTags.includes(tag.slug))
            .map((filteredTags) => filteredTags.name);

        const matchingCategory = items[0].categories.filter(
          (category) => category.slug === slug
        );

        return (
          <Fragment>
            <GridItem
              columnStartSm="1"
              columnEndSm="span 12"
              columnStartMd="1"
              columnEndMd="13"
            >
              <Heading element="h1" variant="tag">
                {filterType === 'tags'
                  ? matchingTags.join(' & ')
                  : matchingCategory[0].name}
              </Heading>
            </GridItem>

            {items.map(
              ({
                id,
                __typename,
                slug: itemSlug,
                title,
                thumbnail,
                image,
                publishDate,
                type,
                mediaType,
                requiresMembership,
                commentsEnabled,
              }) => {
                const newItemKey =
                  items.length >= 8 ? 8 - (items.length % 8) : 1;
                const firstNewItemId = items[items.length - newItemKey].id;
                const isFirstNewItem = id === firstNewItemId;

                let href = itemSlug;

                if (__typename === 'Media' && mediaType === 'video') {
                  href = `/videos/${id}`;
                } else if (__typename === 'Media' && mediaType === 'audio') {
                  href = `/audio/${id}`;
                } else if (type === 'article') {
                  href = `/artikelen/${itemSlug}`;
                }

                const card = (
                  <Link href={href}>
                    {__typename === 'Media' ? (
                      <VideoCard
                        title={title}
                        image={image}
                        publishDate={publishDate}
                        mediaType={mediaType}
                      />
                    ) : (
                      <ArticleCard
                        title={title}
                        thumbnail={thumbnail}
                        publishDate={publishDate}
                        requiresMembership={requiresMembership}
                        commentsEnabled={commentsEnabled}
                      />
                    )}
                  </Link>
                );

                let item = (
                  <GridItem
                    key={id}
                    columnEndSm="span 12"
                    columnEndMd="span 6"
                    columnEndLg="span 3"
                  >
                    {card}
                  </GridItem>
                );

                if (isFirstNewItem) {
                  item = <ScrollIntoView>{item}</ScrollIntoView>;
                }

                return item;
              }
            )}

            {(hasNextPage || hasNextMedia) && (
              <GridItem
                columnStartSm="1"
                columnEndSm="span 12"
                columnStartMd="3"
                columnEndMd="11"
                centered
              >
                <BaseButton
                  variant="underline"
                  onClick={() =>
                    clickHandler(fetchMore, pagesEndCursor, mediaEndCursor)
                  }
                >
                  Toon meer resultaten
                </BaseButton>
              </GridItem>
            )}
          </Fragment>
        );
      }}
    </PageQuery>
  );
};

export default TagOrCategoryItems;

TagOrCategoryItems.propTypes = {
  filterType: PropTypes.oneOf(['tags', 'categories', 'rubrieken']).isRequired,
};
